import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    LOCALE_ID,
    OnDestroy,
    OnInit,
    OnChanges,
    SimpleChanges,
    Output,
} from '@angular/core';
import { LoggerService } from '@klickdata/core/application';
import { MobileService } from '@klickdata/core/mobile';
import { VgApiService } from '@videogular/ngx-videogular/core';
import * as Plyr from 'plyr';
import { Subscription, takeUntil } from 'rxjs';
import { Media } from '../media.model';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';

@Component({
    selector: 'app-media-player',
    templateUrl: './media-player.component.html',
    styleUrls: ['./media-player.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaPlayerComponent extends OnDestroyHandler implements OnInit, OnDestroy, OnChanges {
    @Input() media: Media;
    @Input() mediaCompleted: EventEmitter<boolean>;
    @Input() playerReady: EventEmitter<void>;
    @Input() played: EventEmitter<boolean>;
    @Input() autoplay: boolean = false;
    @Input() hideControls: boolean = false;
    @Input() hasRemoveVideoPlayerOption = false;
    @Output() onPlayerError: EventEmitter<any> = new EventEmitter<any>();
    /**
     * Emitted when the user chooses to remove the media from the error
     * fallback. Parent components should listen to this to delete the item.
     */
    @Output() removeMedia: EventEmitter<Media> = new EventEmitter<Media>();
    private player: Plyr;
    private playerSub: Subscription;
    vgAPI: VgApiService;
    /**
     * Flag raised whenever the underlying player fails to load/play the media
     * (broken link, unsupported format, network issue, ...). When true the
     * template shows a friendly fallback instead of a crashed/blank player.
     */
    public hasError = false;

    /**
     * return ended/completed status.
     */
    get ended(): boolean {
        if (this.player) {
            return this.player.ended;
        } else if (this.vgAPI) {
            return this.vgAPI.getDefaultMedia().isCompleted;
        }
        return true;
    }

    get currentTime(): number {
        const currentTime = this.player
            ? this.player.currentTime
            : this.vgAPI
            ? this.vgAPI.getDefaultMedia().currentTime
            : 0;
        return Math.round(currentTime);
    }

    get mediaType(): string {
        return this.media.provider === 'html5' ? this.media.type : this.media.provider;
    }

    constructor(
        public mobileService: MobileService,
        @Inject(LOCALE_ID) protected localeId: string,
        protected logger: LoggerService,
        protected cdRef: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit() {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['media'] && !changes['media'].firstChange) {
            // A new media was provided, clear any previous failure state.
            this.hasError = false;
            this.updateMediaSource();
        }
    }

    public onInit(player: Plyr) {
        this.player = player;
        // Autoplay if enabled
        if (this.autoplay) {
            const playResult = player.play();
            if (playResult instanceof Promise) {
                playResult.catch((error) => {
                    // Autoplay may fail due to browser policies, ignore silently
                    console.warn('Autoplay failed:', error);
                });
            }
        }
    }

    public onReady() {
        this.playerReady.emit();
    }

    /**
     * Central, crash-safe error handler shared by both players (Plyr & Videogular).
     *
     * A media failure (broken/expired link, unsupported codec, network error,
     * blocked autoplay, ...) must never bubble up and break the surrounding view.
     * We swallow it here, surface a friendly fallback and notify the parent.
     *
     * @todo When player error sometime not fire with vimeo
     * https://github.com/sampotts/plyr/issues/508#issuecomment-332518723
     * @param $event
     */
    public onError($event: any): void {
        try {
            this.hasError = true;
            this.onPlayerError.emit($event);
            this.logger.log($event);
        } catch (handlingError) {
            // Never let the error-handling itself throw and crash the player.
            this.logger.log(handlingError);
        } finally {
            this.cdRef.markForCheck();
        }
    }

    /**
     * Let the user re-attempt loading the media after a failure. Toggling
     * `hasError` re-creates the player template, which re-initializes the
     * underlying player and reloads the current source from scratch.
     */
    public retryLoad(): void {
        this.hasError = false;
        this.player = null;
        this.vgAPI = null;
        this.cdRef.markForCheck();
    }

    /**
     * Notify parent components that the current (failed) media should be
     * removed. The actual deletion is owned by the consumer that provided
     * the media.
     */
    public onRemoveMedia(): void {
        this.removeMedia.emit(this.media);
    }

    public onPlayerReady(api: VgApiService) {
        this.vgAPI = api;
        // Autoplay if enabled
        if (this.autoplay) {
            api.play();
        }

        const defaultMedia = api.getDefaultMedia();
        if (!defaultMedia) {
            return;
        }

        this.playerSub = defaultMedia.subscriptions.play
            .pipe(takeUntil(this.destroy))
            .subscribe(() => this.played.emit(true));

        defaultMedia.subscriptions.ended.pipe(takeUntil(this.destroy)).subscribe(() => this.mediaCompleted.emit(true));

        // Videogular's <vg-player> exposes no (error) output and the native
        // media "error" event does not bubble, so we listen to the media's
        // error stream directly to reliably catch HTML5 video failures.
        defaultMedia.subscriptions.error.pipe(takeUntil(this.destroy)).subscribe(($event) => this.onError($event));
    }

    private updateMediaSource(): void {
        const sourceUrl = this.media?.src ?? this.media?.url;
        const sourceType = this.media?.type;
        const provider = this.media?.provider;

        // Update Plyr-based players (non-html5 providers)
        if (this.player && provider !== 'html5' && sourceUrl && sourceType) {
            this.player.source = {
                type: this.mediaType === 'audio' ? 'audio' : 'video',
                sources: [
                    {
                        src: sourceUrl,
                        type: sourceType,
                        provider,
                    } as any,
                ],
            };

            if (this.autoplay) {
                const playResult = this.player.play();
                if (playResult instanceof Promise) {
                    playResult.catch(() => {
                        // Autoplay can be blocked by browser policies, ignore errors
                    });
                }
            }
        }

        // Update Videogular for html5 video
        if (this.vgAPI && provider === 'html5' && this.media?.mediaType === 'video' && sourceUrl) {
            const defaultMedia = this.vgAPI.getDefaultMedia();
            const mediaElem = defaultMedia?.elem as HTMLVideoElement | undefined;

            if (mediaElem) {
                this.vgAPI.pause();
                mediaElem.src = sourceUrl;
                mediaElem.load();

                if (this.autoplay) {
                    this.vgAPI.play();
                }
            }
        }

        this.cdRef.markForCheck();
    }

    play(currentTime?: number) {
        if (currentTime) {
            this.setCurrentTime(currentTime);
        }

        if (this.player) {
            this.player.play();
        } else if (this.vgAPI) {
            this.vgAPI.play();
        }
    }

    setCurrentTime(currentTime: number) {
        if (this.player) {
            this.player.currentTime = currentTime;
            this.player.muted = false; // fix play after update currentTime cause plays without audio.
        } else if (this.vgAPI) {
            this.vgAPI.currentTime = currentTime;
        }
    }

    pause() {
        if (this.player) {
            this.player.pause();
        } else if (this.vgAPI) {
            this.vgAPI.pause();
        }
        setTimeout(() => this.cdRef.markForCheck());
    }

    seekTime(sec: number, isForward: boolean) {
        if (this.player) {
            isForward ? this.player.forward(sec) : this.player.rewind(sec);
        } else if (this.vgAPI) {
            const offset = isForward ? sec : -sec;
            const newTime = this.currentTime + offset;
            this.vgAPI.seekTime(newTime);
        }
    }

    public get isPlaying(): boolean {
        if (this.player) {
            return this.player.playing;
        } else if (this.vgAPI) {
            return this.vgAPI.state == 'playing';
        }
    }

    ngOnDestroy() {
        if (this.playerSub) {
            this.playerSub.unsubscribe();
            this.playerSub = null;
        }
    }
}
